import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api, HEATMAP_CONSTANTS } from '../api/reportingApi.js';
// import inferenceService from '../../../js/Inspection/services/inferenceService.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.jsx';
import { TrendChart } from '../components/charts/TrendChart.jsx';

// Import the reusable inspection components
import {
    InspectionTable,
    InspectionDetailsModal,
    ImageModal
} from './InspectionComponents.jsx';

import {
    SessionCard,
    SessionDetailsModal
} from './SessionComponents.jsx';

import {
    LotCard,
    LotDetailsModal
} from './LotComponents.jsx';

import { DefectTypeAnalysis } from './DefectTypeAnalysis.jsx';


/**
 * Heatmap aggregation component for a specific Part based on filters.
 */
const ProjectHeatmapAggregation = ({ projectName, filters, addNotification }) => {
    const [availableModels, setAvailableModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState('');
    const [aggregationMethod, setAggregationMethod] = useState('average');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);

    const loadAvailableModels = useCallback(async () => {
        try {
            const combinedFilters = { project: projectName, ...filters };
            const models = await api.getAvailableHeatmapModels(combinedFilters, { bypassCache: true });
            setAvailableModels(models || []);

            if (models && models.length > 0) {
                if (!selectedModel || !models.includes(selectedModel)) {
                    setSelectedModel(models[0]);
                }
            } else {
                setSelectedModel('');
            }
        } catch (error) {
            console.error('Error loading heatmap models for project:', error);
            addNotification({type: 'warning', message: 'Could not load heatmap models.'});
        }
    }, [projectName, filters, addNotification, selectedModel]);


    useEffect(() => {
        if (projectName) {
            loadAvailableModels();
        }
    }, [projectName, filters, loadAvailableModels]);


    const handleGenerateHeatmap = async () => {
        if (!selectedModel) return;

        try {
            setIsLoading(true);
            setResult(null);
            const combinedFilters = { project: projectName, ...filters };
            const heatmapResult = await api.generateAggregatedHeatmap(
                selectedModel,
                aggregationMethod,
                { useCache: true },
                combinedFilters
            );
            setResult(heatmapResult);
            if (heatmapResult?.statistics?.total_inspections > 0) {
                 addNotification({type: 'success', message: `Generated heatmap from ${heatmapResult.statistics.total_inspections} inspections`});
            } else {
                 addNotification({type: 'info', message: 'Heatmap generated, but no matching inspections were found with the selected model.'});
            }
        } catch (error) {
            console.error('Error generating project heatmap:', error);
            addNotification({type: 'danger', message: `Failed to generate heatmap: ${error.message}`});
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = () => {
        if (!result?.aggregated_heatmap) return;
        try {
            const link = document.createElement('a');
            link.href = result.aggregated_heatmap;
            link.download = `project_${projectName}_${selectedModel}_${aggregationMethod}_heatmap.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            addNotification({type: 'success', message: 'Heatmap downloaded successfully'});
        } catch (error) {
            console.error("Download failed", error);
            addNotification({type: 'danger', message: `Failed to download heatmap: ${error.message}`});
        }
    };

    return (
        <div className="card mb-4 shadow-sm">
            <div className="card-header">
                <h5 className="mb-0">
                    <i className="fas fa-fire me-2 text-danger" />
                    Aggregated Heatmap
                </h5>
            </div>
            <div className="card-body">
                 {availableModels.length === 0 ? (
                    <div className="text-center text-muted p-4">
                        <i className="fas fa-fire-extinguisher fa-2x mb-2"></i>
                        <p>No heatmap data available for the current filter selection.</p>
                    </div>
                 ) : (
                    <>
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label small fw-bold">Model</label>
                                <select
                                    className="form-select"
                                    value={selectedModel}
                                    onChange={(e) => setSelectedModel(e.target.value)}
                                    disabled={isLoading}
                                >
                                    {availableModels.map(model => (
                                        <option key={model} value={model}>{model}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-bold">Method</label>
                                <select
                                    className="form-select"
                                    value={aggregationMethod}
                                    onChange={(e) => setAggregationMethod(e.target.value)}
                                    disabled={isLoading}
                                >
                                    {HEATMAP_CONSTANTS.AGGREGATION_METHODS.map(method => (
                                        <option key={method.value} value={method.value}>
                                            {method.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="d-flex gap-2 mb-3">
                            <button
                                className="btn btn-primary"
                                onClick={handleGenerateHeatmap}
                                disabled={!selectedModel || isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm me-2" />
                                        Generating...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-magic me-2" />
                                        Generate Heatmap
                                    </>
                                )}
                            </button>

                            {result && result.aggregated_heatmap && (
                                <button className="btn btn-outline-success" onClick={handleDownload}>
                                    <i className="fas fa-download me-2" />
                                    Download
                                </button>
                            )}
                        </div>

                        {result && result.aggregated_heatmap && (
                             <div className="border rounded p-3 bg-light">
                                <div className="text-center mb-3">
                                    <img 
                                        src={result.aggregated_heatmap} 
                                        alt="Part Aggregated Heatmap"
                                        className="img-fluid rounded"
                                        style={{ maxHeight: '400px' }}
                                    />
                                </div>
                                <div className="text-center">
                                    <small className="text-muted">
                                        Aggregated from {result.statistics.total_inspections} inspections using '{result.statistics.aggregation_method}' method.
                                    </small>
                                </div>
                            </div>
                        )}
                    </>
                 )}
            </div>
        </div>
    );
};


// ===== MAIN COMPONENT =====
const ProjectDetailsPage = ({ 
    projectName: initialProjectName, 
    onNavigateBack, 
    onNavigateToProjects,
    onNavigateToAnalytics 
}) => {
    const { state, setError, addNotification, withManualImagesPref } = useAppContext();
    const { itemsPerPage, defaultDateRange, default_date_range } = state.preferences;
    
    const getProjectNameFromUrl = () => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            return urlParams.get('project');
        }
        return null;
    };
    
    const projectName = initialProjectName || getProjectNameFromUrl();
    
    const [projectData, setProjectData] = useState(null);
    const [trendData, setTrendData] = useState([]);
    const [sessionData, setSessionData] = useState([]);
    const [lotData, setLotData] = useState([]);
    const [inspectionData, setInspectionData] = useState([]);
    const [selectedView, setSelectedView] = useState('overview');
    
    // MINIMAL FILTER CHANGE: Use configuration's default date range
    const [filters, setFilters] = useState({
        dateRange: defaultDateRange || default_date_range || 'month',
        station: '',
        lotId: ''
    });
    
    const [availableOptions, setAvailableOptions] = useState({
        stations: [],
        lots: []
    });
    
    const [isLoading, setIsLoading] = useState(true);

    const [sessionPagination, setSessionPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
    const [lotPagination, setLotPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
    const [inspectionPagination, setInspectionPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });

    const [showSessionModal, setShowSessionModal] = useState(false);
    const [currentSession, setCurrentSession] = useState(null);
    const [sessionInspections, setSessionInspections] = useState([]);
    const [isSessionInspectionsLoading, setIsSessionInspectionsLoading] = useState(false);
    const [sessionInspPagination, setSessionInspPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
    const [sessionTrendData, setSessionTrendData] = useState([]);
    const [isSessionTrendLoading, setIsSessionTrendLoading] = useState(false);

    const [showLotModal, setShowLotModal] = useState(false);
    const [currentLot, setCurrentLot] = useState(null);
    const [lotTrendData, setLotTrendData] = useState([]);
    const [isLotTrendLoading, setIsLotTrendLoading] = useState(false);
    const [lotInspections, setLotInspections] = useState([]);
    const [isLotInspectionsLoading, setIsLotInspectionsLoading] = useState(false);
    const [lotInspPagination, setLotInspPagination] = useState({ 
        page: 1, 
        limit: itemsPerPage || 10, 
        total: 0, 
        totalPages: 1 
    });

    const [modelList, setModelList] = useState([]);
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [showInspectionDetails, setShowInspectionDetails] = useState(false);
    const [largeImage, setLargeImage] = useState(null);
    const [isGroundTruthLoading, setIsGroundTruthLoading] = useState(false);

    const isInitialMount = useRef(true);
    
    // MINIMAL FILTER CHANGE: Add effect to sync with configuration changes
    useEffect(() => {
        if (defaultDateRange || default_date_range) {
            setFilters(prev => ({
                ...prev,
                dateRange: defaultDateRange || default_date_range
            }));
        }
    }, [defaultDateRange, default_date_range]);
    
    useEffect(() => {
        setLotInspPagination(prev => ({ ...prev, limit: itemsPerPage || 10 }));
    }, [itemsPerPage]);

    const handleNavigateToProjects = () => {
        if (onNavigateToProjects) {
            onNavigateToProjects();
        } else if (typeof window !== 'undefined') {
            window.location.href = '../dashboard.html';
        }
    };

    useEffect(() => {
        const fetchModelList = async () => {
            try {
                // const models = await inferenceService.fetchAvailableModels(null);
                const models = await api.getAvailableModels(null);
                setModelList(models || []);
            } catch (error) {
                console.error("[ProjectDetails] Failed to fetch model list:", error);
            }
        };
        fetchModelList();
    }, []);

    const loadViewData = useCallback(async (view, page = 1, options = {}) => {
        if (!projectName) return;
        
        try {
            const baseFilters = { ...filters, project: projectName };
            // FIX: Apply manual images preference to all view data fetches
            const configuredFilters = withManualImagesPref(baseFilters);

            switch (view) {
                case 'sessions':
                    const sessionsResult = await api.getSessions(configuredFilters, page, 10, options);
                    setSessionData(sessionsResult?.data || []);
                    setSessionPagination({
                        page: sessionsResult?.page || page,
                        limit: 10,
                        total: sessionsResult?.total || 0,
                        totalPages: sessionsResult?.totalPages || 1
                    });
                    break;
                    
                case 'lots':
                    const lotsResult = await api.getLots(configuredFilters, page, 10, options);
                    setLotData(lotsResult?.data || []);
                    setLotPagination({
                        page: lotsResult?.page || page,
                        limit: 10,
                        total: lotsResult?.total || 0,
                        totalPages: lotsResult?.total_pages || 1
                    });
                    break;
                    
                case 'reports':
                    const reportsResult = await api.getInspections({
                        ...configuredFilters,
                        sortBy: 'inspection_time',
                        sortDirection: 'desc'
                    }, page, 20, options);
                    setInspectionData(reportsResult?.data || []);
                    setInspectionPagination({
                        page: reportsResult?.page || page,
                        limit: 20,
                        total: reportsResult?.total || 0,
                        totalPages: reportsResult?.total_pages || 1
                    });
                    break;
            }
            
        } catch (error) {
            console.error(`[ProjectDetails] Error loading ${view} data:`, error);
            if (addNotification) {
                addNotification({
                    type: 'danger',
                    message: `Failed to load ${view}: ${error.message}`
                });
            }
        }
    }, [projectName, filters, addNotification, withManualImagesPref]);

    const loadProjectData = useCallback(async (isManualRefresh = false) => {
        if (!projectName) return;
        
        try {
            if (!isManualRefresh) {
                setIsLoading(true);
            }
            setError(null);
            
            const apiOptions = { bypassCache: isManualRefresh };

            // FIX: Apply manual images preference to summary and trend fetches
            const summaryFilters = withManualImagesPref({ ...filters, project: projectName });
            const trendFilters = withManualImagesPref(filters);

            const [summaries, trend] = await Promise.all([
                api.getProjectSummaries(summaryFilters, apiOptions),
                api.getProjectTrend(projectName, trendFilters, apiOptions)
            ]);
            
            const currentProject = summaries.find(p => p.project_name === projectName);
            
            if (!currentProject) {
                setError(`Part "${projectName}" not found`);
                return;
            }
            
            setProjectData(currentProject);
            setTrendData(trend || []);

            if (['sessions', 'lots', 'reports'].includes(selectedView)) {
                await loadViewData(selectedView, 1, apiOptions);
            }
            
            if (isManualRefresh && addNotification) {
                addNotification({ type: 'info', message: 'Part data has been refreshed.' });
            }
            
        } catch (error) {
            console.error('[ProjectDetails] Error loading project data:', error);
            setError(error);
        } finally {
            setIsLoading(false);
        }
    }, [projectName, filters, setError, addNotification, selectedView, loadViewData, withManualImagesPref]);

    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const [stations, lots] = await Promise.all([
                    api.getStations(),
                    projectName ? api.getLotIdentifiers(projectName) : Promise.resolve([])
                ]);
                
                setAvailableOptions({
                    stations: stations || [],
                    lots: lots || []
                });
            } catch (error) {
                console.error('[ProjectDetails] Error loading filter options:', error);
            }
        };
        
        loadFilterOptions();
    }, [projectName]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            loadProjectData(false);
        } else {
            loadProjectData(true);
        }
    }, [loadProjectData]);


    useEffect(() => {
        if (projectData) {
            loadViewData(selectedView, 1, { bypassCache: false });
        }
    }, [selectedView, projectData, loadViewData]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleViewChange = (view) => {
        setSelectedView(view);
    };

    const handleViewSessionDetails = useCallback(async (session) => {
        const sessionId = session.id || session._id;
        if (!sessionId) return;

        setCurrentSession({ ...session, id: sessionId });
        setShowSessionModal(true);
        setIsSessionInspectionsLoading(true);
        setIsSessionTrendLoading(true);

        try {
            // FIX: Apply manual images pref to session inspection fetches
            const inspectionsFilter = withManualImagesPref({ session_id: sessionId, dateRange: 'all' });
            
            const [inspectionsResult, trendResult] = await Promise.all([
                api.getInspections(inspectionsFilter, 1, 10),
                api.getSessionTrend(sessionId, filters.dateRange)
            ]);

            setSessionInspections(inspectionsResult.data || []);
            setSessionInspPagination({ 
                page: inspectionsResult.page || 1, 
                limit: inspectionsResult.limit || 10, 
                total: inspectionsResult.total || 0, 
                totalPages: inspectionsResult.total_pages || 1 
            });
            setSessionTrendData(trendResult || []);

        } catch (err) {
            console.error('[ProjectDetails] getSessionDetails failed', err);
            if (addNotification) {
                addNotification({ type: 'danger', message: `Failed loading session details: ${err.message}` });
            }
        } finally {
            setIsSessionInspectionsLoading(false);
            setIsSessionTrendLoading(false);
        }
    }, [addNotification, filters.dateRange, withManualImagesPref]);

    const handleCloseSessionModal = () => {
        setShowSessionModal(false);
        setCurrentSession(null);
    };

    const handleSessionInspectionPageChange = (newPage) => {
        if (currentSession && newPage >= 1 && newPage <= sessionInspPagination.totalPages) {
            setIsSessionInspectionsLoading(true);
            const inspectionsFilter = withManualImagesPref({ session_id: currentSession.id, dateRange: 'all' });
            api.getInspections(inspectionsFilter, newPage, sessionInspPagination.limit)
                .then(res => {
                    setSessionInspections(res.data || []);
                    setSessionInspPagination(prev => ({ ...prev, page: res.page || newPage }));
                })
                .catch(err => {
                    if (addNotification) {
                        addNotification({ type: 'danger', message: `Failed to load page: ${err.message}` });
                    }
                })
                .finally(() => setIsSessionInspectionsLoading(false));
        }
    };

    const handleViewLotDetails = useCallback(async (lot) => {
        setCurrentLot(lot);
        setShowLotModal(true);
        setIsLotTrendLoading(true);
        setIsLotInspectionsLoading(true);
        
        try {
            // FIX: Apply manual images pref to lot inspection fetches
            const inspectionsFilter = withManualImagesPref({
                lotId: lot.lot_identifier,
                project: projectName,
                sortBy: 'inspection_time',
                sortDirection: 'desc'
            });
            
            const [trendResult, inspectionsResult] = await Promise.all([
                api.getLotTrend(lot.lot_identifier, filters.dateRange),
                api.getInspections(inspectionsFilter, 1, lotInspPagination.limit)
            ]);
            
            setLotTrendData(trendResult || []);
            setLotInspections(inspectionsResult?.data || []);
            setLotInspPagination(prev => ({
                ...prev,
                page: inspectionsResult?.page || 1,
                total: inspectionsResult?.total || 0,
                totalPages: inspectionsResult?.total_pages || 1
            }));
        } catch (error) {
            console.error('[ProjectDetails] Error loading lot details:', error);
            if (addNotification) {
                addNotification({ type: 'danger', message: `Failed to load lot details: ${error.message}` });
            }
        } finally {
            setIsLotTrendLoading(false);
            setIsLotInspectionsLoading(false);
        }
    }, [filters.dateRange, projectName, addNotification, lotInspPagination.limit, withManualImagesPref]);

    const handleCloseLotModal = () => {
        setShowLotModal(false);
        setCurrentLot(null);
    };

    const handleLotInspectionPageChange = (newPage) => {
        if (currentLot && newPage >= 1 && newPage <= lotInspPagination.totalPages) {
            setIsLotInspectionsLoading(true);
            const inspectionsFilter = withManualImagesPref({
                lotId: currentLot.lot_identifier,
                project: projectName,
                sortBy: 'inspection_time',
                sortDirection: 'desc'
            });
            api.getInspections(inspectionsFilter, newPage, lotInspPagination.limit)
                .then(res => {
                    setLotInspections(res.data || []);
                    setLotInspPagination(prev => ({ ...prev, page: res.page || newPage }));
                })
                .catch(err => addNotification({ type: 'danger', message: `Failed to load page: ${err.message}` }))
                .finally(() => setIsLotInspectionsLoading(false));
        }
    };
    
    const handleViewInspectionDetails = useCallback((inspection) => {
        setSelectedInspection(inspection);
        setShowInspectionDetails(true);
    }, []);

    const handleCloseInspectionDetails = useCallback(() => {
        setShowInspectionDetails(false);
        setSelectedInspection(null);
    }, []);

    const handleImageClick = useCallback((imageUrl) => {
        setLargeImage(imageUrl);
    }, []);

    const handleCloseLargeImage = useCallback(() => {
        setLargeImage(null);
    }, []);
    
    const handleSetGroundTruth = useCallback(async (inspectionId, status) => {
        setIsGroundTruthLoading(true);
        try {
            const data = await api.confirmClassification(inspectionId, status);
            const updatedInspection = {
                confirmation_status: status,
                confirmation_timestamp: new Date().toISOString(),
                confirmed_by: data?.username || 'current user'
            };

            // Update all relevant lists
            setInspectionData(prev => prev.map(insp =>
                insp._id === inspectionId ? { ...insp, ...updatedInspection } : insp
            ));
            setSessionInspections(prev => prev.map(insp =>
                insp._id === inspectionId ? { ...insp, ...updatedInspection } : insp
            ));
            setLotInspections(prev => prev.map(insp =>
                insp._id === inspectionId ? { ...insp, ...updatedInspection } : insp
            ));

            if (selectedInspection?._id === inspectionId) {
                setSelectedInspection(prev => ({ ...prev, ...updatedInspection }));
            }
            
            if (addNotification) {
                addNotification({ type: 'success', message: `Ground truth set as ${status}` });
            }
        } catch (error) {
            if (addNotification) {
                addNotification({ type: 'danger', message: `Failed to update: ${error.message}` });
            }
        } finally {
            setIsGroundTruthLoading(false);
        }
    }, [selectedInspection, addNotification]);

    // Pagination handlers
    const handlePageChange = (view, newPage) => {
        if (newPage >= 1) {
            loadViewData(view, newPage);
        }
    };

    if (!projectName) {
        return (
            <div className="container-fluid mt-4">
                <div className="alert alert-warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    No project specified. Please select a project from the projects list.
                    <div className="mt-2">
                        <button className="btn btn-primary" onClick={handleNavigateToProjects}>
                            Go to Parts
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="container-fluid mt-4">
                <LoadingSpinner message="Loading project details..." />
            </div>
        );
    }

    if (!projectData) {
        return (
            <div className="container-fluid mt-4">
                <div className="alert alert-danger">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    Part "{projectName}" not found or no data available.
                    <div className="mt-2">
                        <button className="btn btn-primary" onClick={handleNavigateToProjects}>
                            Back to Parts
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const defectRate = projectData.statistics.total_inspections > 0 
        ? ((projectData.statistics.defect_count / projectData.statistics.total_inspections) * 100).toFixed(1)
        : "0.0";
    
    // FIX: Apply manual images preference to filters passed to child components
    const configuredFiltersForChildren = withManualImagesPref(filters);

    return (
        <ErrorBoundary error={useAppContext().state.error} onRetry={() => loadProjectData(true)}>
            <div className="container-fluid mt-4">
                {/* Header, Filters, Summary Cards... */}
                 <div className="card shadow-sm mb-4">
                    <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="mb-1">
                                    <i className="fas fa-chart-pie me-3 text-primary"></i>
                                    {projectName} Analytics
                                </h2>
                                {projectData.description && (
                                    <p className="text-muted mb-0">{projectData.description}</p>
                                )}
                            </div>
                            <div className="d-flex align-items-center">
                                 <button 
                                    className="btn btn-sm btn-outline-secondary me-2" 
                                    onClick={handleNavigateToProjects}
                                    title="Back to Parts List"
                                >
                                    <i className="fas fa-arrow-left me-2"></i>All Parts
                                </button>
                                <button 
                                    className="btn btn-sm btn-primary" 
                                    onClick={() => loadProjectData(true)}
                                    title="Refresh data"
                                >
                                    <i className="fas fa-sync-alt me-1"></i> Refresh
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* MINIMAL FILTER CHANGE: Keep original filter panel structure, just show current value properly */}
                <div className="card shadow-sm mb-4">
                    <div className="card-header bg-light">
                        <h5 className="mb-0">
                            <i className="fas fa-filter text-primary me-2"></i>Filters
                        </h5>
                    </div>
                    <div className="card-body">
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label className="form-label small fw-bold">Date Range</label>
                                <select 
                                    className="form-select form-select-sm" 
                                    value={filters.dateRange}
                                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                                >
                                    <option value="today">Today</option>
                                    <option value="week">Last 7 Days</option>
                                    <option value="month">Last 30 Days</option>
                                    <option value="year">Last Year</option>
                                    <option value="all">All Time</option>
                                </select>
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label small fw-bold">Station</label>
                                <select 
                                    className="form-select form-select-sm" 
                                    value={filters.station}
                                    onChange={(e) => handleFilterChange('station', e.target.value)}
                                >
                                    <option value="">All Stations</option>
                                    {availableOptions.stations?.map(s => (
                                        <option key={s.station_id} value={s.station_id}>
                                            {s.station_name || s.station_id}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-4 mb-3">
                                <label className="form-label small fw-bold">Lot ID</label>
                                <select 
                                    className="form-select form-select-sm" 
                                    value={filters.lotId}
                                    onChange={(e) => handleFilterChange('lotId', e.target.value)}
                                >
                                    <option value="">All Lots</option>
                                    {availableOptions.lots?.map(lot => (
                                        <option key={lot} value={lot}>{lot}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row mb-4">
                    <div className="col-md-3 mb-3">
                        <div className="card h-100 shadow-sm border-primary">
                            <div className="card-body text-center">
                                <i className="fas fa-microscope fa-2x text-primary mb-2"></i>
                                <h3 className="text-primary mb-1">{projectData.statistics.total_inspections.toLocaleString()}</h3>
                                <p className="text-muted mb-0">Total Inspections</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card h-100 shadow-sm border-danger">
                            <div className="card-body text-center">
                                <i className="fas fa-exclamation-triangle fa-2x text-danger mb-2"></i>
                                <h3 className="text-danger mb-1">{projectData.statistics.defect_count.toLocaleString()}</h3>
                                <p className="text-muted mb-0">Defects Found</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card h-100 shadow-sm border-warning">
                            <div className="card-body text-center">
                                <i className="fas fa-percentage fa-2x text-warning mb-2"></i>
                                <h3 className={`mb-1 ${parseFloat(defectRate) > 5 ? 'text-danger' : 'text-success'}`}>
                                    {defectRate}%
                                </h3>
                                <p className="text-muted mb-0">Defect Rate</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3 mb-3">
                        <div className="card h-100 shadow-sm border-info">
                            <div className="card-body text-center">
                                <i className="fas fa-calendar fa-2x text-info mb-2"></i>
                                <h3 className="text-info mb-1">
                                    {projectData.last_inspection 
                                        ? new Date(projectData.last_inspection).toLocaleDateString()
                                        : 'N/A'
                                    }
                                </h3>
                                <p className="text-muted mb-0">Last Inspection</p>
                            </div>
                        </div>
                    </div>
                </div>

                <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                        <button className={`nav-link ${selectedView === 'overview' ? 'active' : ''}`} onClick={() => handleViewChange('overview')}>
                            <i className="fas fa-chart-line me-2"></i>Overview
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${selectedView === 'sessions' ? 'active' : ''}`} onClick={() => handleViewChange('sessions')}>
                            <i className="fas fa-play-circle me-2"></i>Sessions
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${selectedView === 'lots' ? 'active' : ''}`} onClick={() => handleViewChange('lots')}>
                            <i className="fas fa-boxes me-2"></i>Lots
                        </button>
                    </li>
                    <li className="nav-item">
                        <button className={`nav-link ${selectedView === 'reports' ? 'active' : ''}`} onClick={() => handleViewChange('reports')}>
                            <i className="fas fa-file-alt me-2"></i>Raw Inspection Data
                        </button>
                    </li>
                </ul>

                {selectedView === 'overview' && (
                    <>
                        <div className="row">
                            <div className="col-lg-8 mb-4">
                                <div className="card shadow-sm h-100">
                                    <div className="card-header"><h5 className="mb-0"><i className="fas fa-chart-line me-2"></i>Performance Trend</h5></div>
                                    <div className="card-body d-flex align-items-center justify-content-center">
                                        <div style={{ height: '400px', width: '100%' }}><TrendChart chartId="part-overview-trend" data={trendData} /></div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-4 mb-4">
                                <div className="card shadow-sm h-100">
                                    <div className="card-header"><h6 className="mb-0">Status</h6></div>
                                    <div className="card-body">
                                        <table className="table table-sm mb-0">
                                            <tbody>
                                                <tr><td><strong>Status:</strong></td><td><span className={`badge ${projectData.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>{projectData.status}</span></td></tr>
                                                <tr><td><strong>Trained:</strong></td><td><span className={`badge ${projectData.trained ? 'bg-success' : 'bg-warning'}`}>{projectData.trained ? 'Yes' : 'No'}</span></td></tr>
                                                <tr><td><strong>Last Activity:</strong></td><td>{projectData.last_inspection ? new Date(projectData.last_inspection).toLocaleString() : 'No recent activity'}</td></tr>
                                                <tr><td><strong>Success Rate:</strong></td><td className="fw-bold text-success">{(100 - parseFloat(defectRate)).toFixed(1)}%</td></tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mt-4">
                            <div className="col-12">
                                <DefectTypeAnalysis filters={withManualImagesPref({ ...filters, project: projectName })} title={`Overall Defect Type Analysis for ${projectName}`} addNotification={addNotification}/>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <ProjectHeatmapAggregation projectName={projectName} filters={configuredFiltersForChildren} addNotification={addNotification}/>
                            </div>
                        </div>
                    </>
                )}

                {/* Other views (Sessions, Lots, Reports) */}
                {selectedView === 'sessions' && (
                    <div className="card shadow-sm">
                        <div className="card-header"><div className="d-flex justify-content-between align-items-center"><h5 className="mb-0"><i className="fas fa-history text-primary me-2"></i>Inspection Sessions</h5><div className="d-flex align-items-center gap-3"><small className="text-muted">Page {sessionPagination.page} of {sessionPagination.totalPages} ({sessionPagination.total.toLocaleString()} total)</small><button className="btn btn-sm btn-outline-primary" onClick={() => loadViewData('sessions', 1, { bypassCache: true })} title="Refresh sessions"><i className="fas fa-sync-alt"></i></button></div></div></div>
                        <div className="card-body">
                            {sessionData.length > 0 ? (
                                <><div className="sessions-list">{sessionData.map((session) => <SessionCard key={session.id} session={session} onViewDetails={handleViewSessionDetails} projectName={projectName}/>)}</div>
                                {sessionPagination.totalPages > 1 && <div className="card shadow-sm mt-3"><div className="card-body"><nav className="d-flex justify-content-center"><ul className="pagination mb-0"><li className={`page-item ${sessionPagination.page === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('sessions', 1)} disabled={sessionPagination.page === 1}>«</button></li><li className={`page-item ${sessionPagination.page === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('sessions', sessionPagination.page - 1)} disabled={sessionPagination.page === 1}>‹ Prev</button></li><li className="page-item active"><span className="page-link">{sessionPagination.page}</span></li><li className={`page-item ${sessionPagination.page >= sessionPagination.totalPages ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('sessions', sessionPagination.page + 1)} disabled={sessionPagination.page >= sessionPagination.totalPages}>Next ›</button></li><li className={`page-item ${sessionPagination.page >= sessionPagination.totalPages ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('sessions', sessionPagination.totalPages)} disabled={sessionPagination.page >= sessionPagination.totalPages}>»</button></li></ul></nav></div></div>}</>
                            ) : <div className="card shadow-sm"><div className="card-body text-center p-5"><i className="fas fa-history fa-4x text-muted mb-4"></i><h4 className="text-muted">No Sessions Found</h4><p className="text-muted">No sessions match the current filters.</p></div></div>}
                        </div>
                    </div>
                )}

                {selectedView === 'lots' && (
                    <div className="card shadow-sm">
                        <div className="card-header"><div className="d-flex justify-content-between align-items-center"><h5 className="mb-0"><i className="fas fa-boxes me-2"></i>Lot Performance</h5><small className="text-muted">Page {lotPagination.page} of {lotPagination.totalPages} ({lotPagination.total} total)</small></div></div>
                        <div className="card-body">
                            {lotData.length > 0 ? (
                                <><div className="lots-list">{lotData.map(lot => <LotCard key={lot.lot_identifier} lot={lot} onViewDetails={handleViewLotDetails}/>)}</div>
                                {lotPagination.totalPages > 1 && <div className="d-flex justify-content-center mt-3"><nav><ul className="pagination mb-0"><li className={`page-item ${lotPagination.page === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('lots', 1)} disabled={lotPagination.page === 1}>«</button></li><li className={`page-item ${lotPagination.page === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('lots', lotPagination.page - 1)} disabled={lotPagination.page === 1}>‹ Prev</button></li><li className="page-item active"><span className="page-link">{lotPagination.page}</span></li><li className={`page-item ${lotPagination.page >= lotPagination.totalPages ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('lots', lotPagination.page + 1)} disabled={lotPagination.page >= lotPagination.totalPages}>Next ›</button></li><li className={`page-item ${lotPagination.page >= lotPagination.totalPages ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('lots', lotPagination.totalPages)} disabled={lotPagination.page >= lotPagination.totalPages}>»</button></li></ul></nav></div>}</>
                            ) : <div className="text-center py-4"><i className="fas fa-boxes fa-3x text-muted mb-3"></i><h5 className="text-muted">No Lots Found</h5><p className="text-muted">No lots match the current filters.</p></div>}
                        </div>
                    </div>
                )}

                {selectedView === 'reports' && (
                    <div>
                        <InspectionTable inspections={inspectionData} onViewDetails={handleViewInspectionDetails} onImageClick={handleImageClick} onSetGroundTruth={handleSetGroundTruth} isLoading={isGroundTruthLoading} showExportButtons={true} title={`Inspection Data for ${projectName}`} addNotification={addNotification}/>
                        {inspectionPagination.totalPages > 1 && <div className="card shadow-sm mt-3"><div className="card-body"><div className="d-flex justify-content-between align-items-center"><div className="text-muted small">Showing {((inspectionPagination.page - 1) * inspectionPagination.limit) + 1} to {Math.min(inspectionPagination.page * inspectionPagination.limit, inspectionPagination.total)} of {inspectionPagination.total.toLocaleString()}</div><nav><ul className="pagination pagination-sm mb-0"><li className={`page-item ${inspectionPagination.page === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('reports', 1)} disabled={inspectionPagination.page === 1}>«</button></li><li className={`page-item ${inspectionPagination.page === 1 ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('reports', inspectionPagination.page - 1)} disabled={inspectionPagination.page === 1}>‹</button></li><li className="page-item active"><span className="page-link">{inspectionPagination.page} of {inspectionPagination.totalPages}</span></li><li className={`page-item ${inspectionPagination.page >= inspectionPagination.totalPages ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('reports', inspectionPagination.page + 1)} disabled={inspectionPagination.page >= inspectionPagination.totalPages}>›</button></li><li className={`page-item ${inspectionPagination.page >= inspectionPagination.totalPages ? 'disabled' : ''}`}><button className="page-link" onClick={() => handlePageChange('reports', inspectionPagination.totalPages)} disabled={inspectionPagination.page >= inspectionPagination.totalPages}>»</button></li></ul></nav></div></div></div>}
                    </div>
                )}

                {/* Modals */}
                <SessionDetailsModal session={currentSession} isOpen={showSessionModal} onClose={handleCloseSessionModal} trendData={sessionTrendData} isTrendLoading={isSessionTrendLoading} sessionInspections={sessionInspections} isInspectionsLoading={isSessionInspectionsLoading} onViewInspectionDetails={handleViewInspectionDetails} onImageClick={handleImageClick} onSetGroundTruth={handleSetGroundTruth} isGroundTruthLoading={isGroundTruthLoading} addNotification={addNotification} inspectionPagination={sessionInspPagination} onInspectionPageChange={handleSessionInspectionPageChange}/>
                <LotDetailsModal lot={currentLot} isOpen={showLotModal} onClose={handleCloseLotModal} trendData={lotTrendData} isTrendLoading={isLotTrendLoading} recentInspections={lotInspections} isInspectionsLoading={isLotInspectionsLoading} onViewInspectionDetails={handleViewInspectionDetails} onImageClick={handleImageClick} onSetGroundTruth={handleSetGroundTruth} isGroundTruthLoading={isGroundTruthLoading} addNotification={addNotification} inspectionPagination={lotInspPagination} onInspectionPageChange={handleLotInspectionPageChange}/>
                {showInspectionDetails && selectedInspection && <InspectionDetailsModal inspection={selectedInspection} modelList={modelList} onClose={handleCloseInspectionDetails} onImageClick={handleImageClick} onSetGroundTruth={handleSetGroundTruth} isLoading={isGroundTruthLoading} addNotification={addNotification}/>}
                <ImageModal src={largeImage} alt="Enlarged view" show={!!largeImage} onClose={handleCloseLargeImage}/>
            </div>
        </ErrorBoundary>
    );
};

export default ProjectDetailsPage;